import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';

import {
  getOpportunities,
  getOpportunitiesStats,
  mapOpportunityDtoToCardItem,
} from './opportunity.api';
import { OPPORTUNITIES_QUERY_KEYS, type OpportunitiesRoomQueryFilters } from './query-keys';
import type { OpportunityCardItem } from '../model/types';
import { SEVEN_DAYS_MS } from '@/shared/constants/time.constants';

const PAGE_SIZE = 20;

type GetOpportunitiesResponse = Awaited<ReturnType<typeof getOpportunities>>;

interface OpportunitiesRoomPage {
  items: OpportunityCardItem[];
  total: number;
}

const defaultRoomSort = {
  sortBy: 'created_at',
  sortOrder: 'DESC',
} as const;

function mapPage(
  response: GetOpportunitiesResponse,
  skip: number,
  topRankedCount: number,
): OpportunitiesRoomPage {
  const items = response.items.map(
    (dto, index): OpportunityCardItem => ({
      ...mapOpportunityDtoToCardItem(dto),
      topRanked: skip === 0 && index < topRankedCount,
    }),
  );
  return { items, total: response.total };
}

/**
 * Top 20 opportunities by recency from the last 7 days.
 * Used by the dashboard preview strip.
 */
export const opportunitiesPreviewQuery = (workspaceId: string | null) =>
  queryOptions({
    queryKey: OPPORTUNITIES_QUERY_KEYS.allByWorkspace(workspaceId),
    queryFn: async () => {
      if (!workspaceId) throw new Error('Workspace ID required');
      const response = await getOpportunities({
        workspaceId,
        skip: 0,
        take: PAGE_SIZE,
        sortBy: 'created_at',
        sortOrder: 'DESC',
        dateStart: new Date(Date.now() - SEVEN_DAYS_MS).toISOString(),
      });
      return mapPage(response, 0, 5).items;
    },
    enabled: Boolean(workspaceId),
    staleTime: 5 * 60 * 1000,
  });
/**
 * Unfiltered KPI aggregates (total / new-since-last-visit / count-by-duration-type)
 * for the Opportunities Room, computed server-side in a single request.
 *
 * `since` is the caller's "last visited" baseline; it feeds `newCount` and is
 * part of the query key so the count stays correct when the baseline changes.
 */
export const opportunitiesRoomKpiQuery = (workspaceId: string | null, since?: string) =>
  queryOptions({
    queryKey: OPPORTUNITIES_QUERY_KEYS.roomKpi(workspaceId, since),
    queryFn: async () => {
      if (!workspaceId) throw new Error('Workspace ID required');
      return getOpportunitiesStats({ workspaceId, since });
    },
    enabled: Boolean(workspaceId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

/**
 * Paginated opportunities sorted by recency, 20 per page.
 * Used by the Opportunities Room page with infinite scroll.
 */
export const opportunitiesRoomInfiniteQuery = (
  workspaceId: string | null,
  filters?: OpportunitiesRoomQueryFilters,
) =>
  infiniteQueryOptions({
    queryKey: OPPORTUNITIES_QUERY_KEYS.room(workspaceId, filters),
    queryFn: async ({ pageParam: skip }) => {
      if (!workspaceId) throw new Error('Workspace ID required');
      const response = await getOpportunities({
        workspaceId,
        skip,
        take: PAGE_SIZE,
        sortBy: filters?.sortBy ?? defaultRoomSort.sortBy,
        sortOrder: filters?.sortOrder ?? defaultRoomSort.sortOrder,
        favoritesOnly: filters?.favoritesOnly,
        dateStart: filters?.dateStart,
        dateEnd: filters?.dateEnd,
        topic: filters?.topic,
        durationTypes: filters?.durationTypes,
        insightType: filters?.insightType,
        market: filters?.market,
        audience: filters?.audience,
        search: filters?.search,
      });
      return mapPage(response, skip, 0);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.items.length < PAGE_SIZE) return undefined;
      return allPages.reduce((sum, page) => sum + page.items.length, 0);
    },
    enabled: Boolean(workspaceId),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
